import React, { useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import { FROM_MOBILE_APP_FLAG, routePaths } from "App/modules/urls";
import { DiscoverPage } from "./Discover";
import { DiscoverPageItemsProvider } from "./Discover/useDiscoverPageItems";
import { Library } from "./Library";
import Space from "./Space";
import { SpaceContextProvider, useSpaceContext } from "App/common/useSpace";
import { Layout } from "App/components/Layout";
import { Settings } from "../Settings";
import { EntityNotFound } from "./EntityNotFound";
import { getAuthUrl, useAuth } from "App/components/Auth";

import { NoSpaces } from "./Space/NoSpaces";
import LoadingScreen from "App/common/LoadingScreen/LoadingScreen";
import { FileUploadsContextProvider } from "App/components/useFiles";
import { useHighnote } from "App/common/useHighnote";
import { useUrlContext } from "App/routes/Main/useUrlContext";
import { AuthRedirectRequired } from "App/modules/Auth/AuthRedirectRequired";
import { NotificationsProvider } from "App/common/useNotifications";
import { SpaceEditorProvider } from "App/components/useSpaceEditor";
import { UsageEditorProvider } from "App/components/Usages/UsageEditor";
import { TopLevelSpacesContextProvider } from "App/components/useEntities/useTopLevelSpaces";
import { useRevocationTeardown } from "App/components/Auth/useRevocationTeardown";
import { GlobalSpacesContextProvider } from "App/store/spaces/useGlobalSpaces";
import { GlobalTracksContextProvider } from "App/store/tracks/useGlobalTracks";
import { GlobalSpaceTracksContextProvider } from "App/store/spaceTracks/useGlobalSpaceTracks";
import { MoveToSpaceProvider } from "App/components/useEntities/useLibraryTracks/useMoveToSpace";
import { AddToSpaceProvider } from "App/components/useEntities/useLibraryTracks/useAddToSpace";
import { DownloadRequestPage } from "./DownloadRequest";
import { DropboxClone } from "../Dropbox/Clone";
import { DropboxLogin } from "../Dropbox/Login";
import { DropboxCallback } from "../Dropbox/Callback";
import { Submit } from "../Submit/Submit";
import { GlobalAudioPlayerContextProvider } from "App/components/GlobalAudioPlayer";
import { MoveSelectionsToTargetProvider } from "App/components/useEntities/useMoveSelectionsToTarget";
import { TrackVersionEditorProvider } from "App/components/TrackVersionEditor/utils/context/TrackVersionEditorContext";
import MobileAppAuthRedirect from "./MobileAppAuthRedirect/MobileAppAuthRedirect";
import { DnDMonitorProvider } from "App/components/DragAndDrop/DnDMonitor";
import { GenerateComments } from "App/routes/GenerateComments/GenerateComments";

const SpaceRedirect = () => {
  const { spaceId, trackId } = useUrlContext();
  const history = useHistory();
  const { space } = useSpaceContext();
  const { user: currentUser } = useAuth();

  useEffect(() => {
    if (!space || space?.id !== spaceId) return;
    if (trackId) return;
    history.replace(`/space/${spaceId}${window.location.search}`);
  }, [trackId, space]);

  const render = useMemo(() => {
    if (spaceId) {
      return <Space />;
    }

    if (!currentUser) {
      return <Redirect to={getAuthUrl()} />;
    }

    return <NoSpaces />;
  }, [spaceId, currentUser]);

  return render;
};

// Only allow one automatically created space per session.
let autocreatedSpaceId: string;

const CreateSpace = () => {
  const { createSpace } = useHighnote();
  const history = useHistory();
  const { user } = useAuth();
  const isLoggedIn = !!user;

  useEffect(() => {
    if (!user) return;
    if (autocreatedSpaceId) return;

    createSpace().then((space) => {
      autocreatedSpaceId = space.id;
      history.push(`/space/${space.id}`);
    });
  }, [user]);

  if (!isLoggedIn || autocreatedSpaceId) {
    return <Redirect to={getAuthUrl()} />;
  }

  return <LoadingScreen />;
};

const HighnoteCore = () => {
  return (
    <Switch>
      <Route path={routePaths.space}>
        <SpaceRedirect />
      </Route>
      <Redirect
        exact
        path={routePaths.collectionRoot}
        to={{
          pathname: routePaths.spaceRoot,
          search: window.location.search,
        }}
      />
      <Redirect
        path={routePaths.collectionHome}
        to={{
          pathname: `${routePaths.spaceRoot}/:collectionId`,
          search: window.location.search,
        }}
      />
      <Route path={routePaths.home}>
        <DiscoverPage />
      </Route>
      <Route path={routePaths.library}>
        <Library />
      </Route>
      <Route path={routePaths.trackstories}>
        <GenerateComments />
      </Route>
      <Route path={routePaths.download}>
        <DownloadRequestPage />
      </Route>
      <Route path={routePaths.dropboxLogin}>
        <AuthRedirectRequired>
          <DropboxLogin />
        </AuthRedirectRequired>
      </Route>
      <Route path={routePaths.dropboxCallback}>
        <AuthRedirectRequired includeSearch>
          <DropboxCallback />
        </AuthRedirectRequired>
      </Route>
      <Route path={routePaths.dropboxClone}>
        <AuthRedirectRequired includeSearch>
          <DropboxClone />
        </AuthRedirectRequired>
      </Route>
      <Route path={routePaths.submit}>
        <Submit />
      </Route>
      <Route render={() => <EntityNotFound entityType="page" />} />
    </Switch>
  );
};

const HighnoteRoot = () => {
  const isFromMobileApp =
    sessionStorage.getItem(FROM_MOBILE_APP_FLAG) === "true";

  if (isFromMobileApp) {
    return <Redirect to={routePaths.mobileLoginRedirect} />;
  }

  // Everyone — logged in or out, mobile or desktop — lands on /home
  // (the Discover page).
  return <Redirect to={routePaths.home} />;
};

/**
 * Mounts the hook that tears down space and track watchers when the user
 * loses access. It renders nothing.
 *
 * It must sit below both `GlobalSpacesContextProvider` and
 * `GlobalTracksContextProvider`: it needs `useGlobalSpaces`,
 * `useChildEntityWatchers`, and the router, none of which exist above Auth.
 *
 * Warning: `useChildEntityWatchers` calls `useGlobalTracks()` as well as
 * `useGlobalSpaces()` (it detaches track watchers as well as space
 * watchers), and `useGlobalTracks()` throws when it runs outside its
 * provider. Mounted one level too high (between the two providers), this
 * component threw on every render of `Main`, for every user, on every
 * route. The flag gates the effect body, not the hook calls above it, so
 * `spaceMembership: "legacy"` did not save it. The throw escaped to the
 * top-level boundary in `App.jsx`, which renders no fallback. React then
 * unmounted the whole tree, and every route under `Main` rendered a blank
 * page.
 */
const RevocationTeardown = (): null => {
  useRevocationTeardown();
  return null;
};

const Main = () => {
  return (
    <>
      <Helmet>
        <title>Highnote | Gather feedback on your tracks</title>
      </Helmet>
      <FileUploadsContextProvider>
        <TopLevelSpacesContextProvider>
          <GlobalSpacesContextProvider>
            <GlobalTracksContextProvider>
              <GlobalSpaceTracksContextProvider>
                {/*
                 * INSIDE the join-row provider, not above it. RevocationTeardown
                 * calls useChildEntityWatchers, which calls useGlobalSpaceTracks
                 * unconditionally (store/helpers/useChildEntityWatchers.ts), and
                 * that hook throws outside its provider
                 * (store/spaceTracks/useGlobalSpaceTracks.tsx). On the membership
                 * branch this component is the first child of
                 * GlobalTracksContextProvider, which is the anchor this stack
                 * replaced; leaving it there crashes the whole app at mount, on
                 * every route, with every membership flag off.
                 */}
                <RevocationTeardown />
                <MoveToSpaceProvider>
                  <MoveSelectionsToTargetProvider>
                    <AddToSpaceProvider>
                      <SpaceContextProvider>
                        <SpaceEditorProvider>
                          <NotificationsProvider>
                            <UsageEditorProvider>
                              <TrackVersionEditorProvider>
                                <GlobalAudioPlayerContextProvider>
                                  <DnDMonitorProvider>
                                    <DiscoverPageItemsProvider>
                                      <Layout>
                                        <Switch>
                                          <Route
                                            path={routePaths.createSpace}
                                            component={CreateSpace}
                                          />
                                          <Route
                                            path={routePaths.settings}
                                            component={Settings}
                                          />
                                          <Route path={routePaths.root} exact>
                                            <HighnoteRoot />
                                          </Route>
                                          <Route
                                            path={
                                              routePaths.mobileLoginRedirect
                                            }
                                          >
                                            <MobileAppAuthRedirect />
                                          </Route>
                                          <Route path={routePaths.root}>
                                            <HighnoteCore />
                                          </Route>
                                        </Switch>
                                      </Layout>
                                    </DiscoverPageItemsProvider>
                                  </DnDMonitorProvider>
                                </GlobalAudioPlayerContextProvider>
                              </TrackVersionEditorProvider>
                            </UsageEditorProvider>
                          </NotificationsProvider>
                        </SpaceEditorProvider>
                      </SpaceContextProvider>
                    </AddToSpaceProvider>
                  </MoveSelectionsToTargetProvider>
                </MoveToSpaceProvider>
              </GlobalSpaceTracksContextProvider>
            </GlobalTracksContextProvider>
          </GlobalSpacesContextProvider>
        </TopLevelSpacesContextProvider>
      </FileUploadsContextProvider>
    </>
  );
};

export default Main;
